import './bootstrap'
import '../css/app.css'

// Import GTM Performance Optimizer
import './gtm-optimizer'


import { createApp, defineAsyncComponent, h } from 'vue'
import {createInertiaApp, router, usePage} from '@inertiajs/vue3'
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers'
import { ZiggyVue } from 'ziggy-js';
import { createPinia } from 'pinia'
// import Layout from '@/Layouts/MasterLayout.vue'
import Layout from '@/Layouts/MasterLayoutV2.vue'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginVue from '@bugsnag/plugin-vue'

// What is deliberately NOT imported here any more (NEX-9357 slow-4G pass on the shared entry):
//  - vue-tailwind-datepicker and @vuepic/vue-datepicker: ~300 KB of source, zero users in any
//    Vue page (the admin report entries under resources/js/admin bundle their own).
//  - PrimeVue + its Tailwind passthrough preset: no page imports a primevue component; only the
//    non-functional ssr.js still registers it.
//  - FontAwesome svg-core + icons: only Home, Reels and VerticalClips render icons, so the library
//    is an async component (plugins/fontawesome.js) prefetched for those pages below.
//  - @bugsnag/browser-performance: started after `load` via dynamic import — it reads the
//    Navigation Timing entries retroactively, so nothing is lost by starting it late.
// Removing all four took ~250 KB (gzipped ~90 KB) off every Inertia page's first load.

/**
 * Pages whose first paint includes <font-awesome-icon>s. Their icon chunk is fetched alongside
 * the page component instead of on first render, so nothing pops in a frame late. Every other
 * page still gets the icons — on demand, the first time one renders (a Toast, a share popup).
 */
const FA_PREFETCH_PAGES = /^(Home|Reels|VerticalClips)\//
const loadFontAwesome = () => import('./plugins/fontawesome.js')

const appName = window.document.getElementsByTagName('title')[0]?.innerText || 'Laravel'
const pinia = createPinia()

// Debug: Log the app name
console.log('App name:', appName)

// The Typekit stylesheet (neue-haas-grotesk) is now a non-blocking <link> in app2.blade.php's
// head, so the font request starts with the HTML instead of after this bundle has executed.

// Initialize Bugsnag
if (import.meta.env.VITE_BUGSNAG_API_KEY) {
    Bugsnag.start({
        apiKey: import.meta.env.VITE_BUGSNAG_API_KEY,
        plugins: [new BugsnagPluginVue()],
        enabledReleaseStages: ['production'],
        releaseStage: import.meta.env.VITE_APP_ENV,
        onError: (event) => {
            // Add navigation context
            event.addMetadata('navigation', {
                url: window.location.href,
                path: window.location.pathname,
                referrer: document.referrer || null,
                userAgent: navigator.userAgent,
                online: navigator.onLine,
                visibility: document.visibilityState,
            })

            // Enrich Axios errors with request/response details
            const originalError = event.originalError
            if (originalError?.isAxiosError) {
                const { config, response, message } = originalError
                event.addMetadata('axios_request', {
                    method: config?.method?.toUpperCase(),
                    url: config?.url,
                    baseURL: config?.baseURL,
                    timeout: config?.timeout,
                    params: config?.params,
                })
                if (response) {
                    const body = typeof response.data === 'string'
                        ? response.data.substring(0, 1000)
                        : JSON.stringify(response.data)?.substring(0, 1000) || null
                    event.addMetadata('axios_response', {
                        status: response.status,
                        statusText: response.statusText,
                        body: body,
                    })
                } else {
                    event.addMetadata('axios_response', {
                        message: message,
                        code: originalError.code,
                    })
                }
            }
        },
    })

    // Performance monitoring is not on the critical path: start it once the page has loaded.
    // It reads the Navigation Timing entries retroactively, so the page-load span is intact.
    const startBugsnagPerformance = () => import('@bugsnag/browser-performance')
        .then(({ default: BugsnagPerformance }) => BugsnagPerformance.start({ apiKey: import.meta.env.VITE_BUGSNAG_API_KEY }))
        .catch(() => {})
    if (document.readyState === 'complete') {
        startBugsnagPerformance()
    } else {
        window.addEventListener('load', startBugsnagPerformance, { once: true })
    }
}

createInertiaApp({
    title: title => {
        const fullTitle = `${title} - ${appName}`
        return fullTitle
    },
    resolve: name =>
        Promise.all([
            resolvePageComponent(`./Pages/${name}.vue`, import.meta.glob('./Pages/**/*.vue')),
            FA_PREFETCH_PAGES.test(name) ? loadFontAwesome().catch(() => null) : null,
        ]).then(
            ([page]) => {
                page.default.layout = page.default.layout || Layout

                return page
            },
        ),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) })
            .use(plugin)
            .use(ZiggyVue)
            .use(pinia)
            .use(Bugsnag.getPlugin('vue'))
            // Same tag every template already uses; the library arrives with the first render.
            .component('font-awesome-icon', defineAsyncComponent(loadFontAwesome))

        // Global right-click handler (production only)
        app.config.globalProperties.$disableRightClick = (event) => {
            try {
                const isProduction = props?.initialPage?.props?.app?.env === 'production'
                if (isProduction) {
                    if (event && typeof event.preventDefault === 'function') {
                        event.preventDefault()
                    }
                    return false
                }
            } catch (e) {
                // no-op
            }
            return true
        }

        app.mount(el)
    },
    progress: {
        // The delay after which the progress bar will appear, in milliseconds...
        delay: 250,

        // The color of the progress bar...
        color: '#0052cc',

        // Whether to include the default NProgress styles...
        includeCSS: true,

        // Whether the NProgress spinner will be shown...
        showSpinner: false,
    },
})

// Global event listener for router error handling.
// Inertia v3 renamed the 'invalid' event to 'httpException'. The event detail's
// `response` is now an HttpExceptionResponse (status, data, headers) from Inertia's
// built-in XHR client — there is no longer a `statusText` field (it was axios-only).
router.on('httpException', (event) => {
    console.log('Invalid Inertia Response Received:', event.detail.response);

    const page = usePage();
    const isProduction = page.props?.app?.env === 'production' ||
        page.props?.app?.isProduction === true;

    // Prevent the default error modal in production only
    if (isProduction) {
        if (import.meta.env.VITE_BUGSNAG_API_KEY) {
            const response = event.detail.response;
            Bugsnag.notify(new Error('Invalid Inertia Response Received'), function (bugsnagEvent) {
                bugsnagEvent.severity = 'error';
                bugsnagEvent.addMetadata('request', {
                    url: window.location.href,
                });
                // v3: response.data may be a string or a parsed object.
                const body = typeof response?.data === 'string'
                    ? response.data.substring(0, 1000)
                    : JSON.stringify(response?.data)?.substring(0, 1000) || null;
                bugsnagEvent.addMetadata('response', {
                    status: response?.status,
                    headers: response?.headers || {},
                    body: body,
                });
            })
        }
        event.preventDefault();
    }

    window.dispatchEvent(new CustomEvent('show-toast', {
        detail: {
            message: 'An unexpected error occurred. Please try again.',
            icon: 'fas fa-circle-info',
            duration: 5000
        }
    }))
});

axios.interceptors.response.use(
    response => response,
    error => {
        if (error.response?.status === 401) {
            const intendedUrl = window.location.pathname + window.location.search;

            window.location.href = '/login' + intendedUrl ? '?dest=' + intendedUrl : '';
        }
        return Promise.reject(error);
    }
);
